import type { CSSProperties } from "react"

import { cn } from "@/domains/library"

import {
  FOOTER_TRON_GRID_NODES,
  FOOTER_TRON_PLUS_PARTICLES,
  HOMEPAGE_TRON_GRID_NODES,
  HOMEPAGE_TRON_PLUS_PARTICLES,
  TRON_GRID_VIEWBOX,
  type TronGridVariant,
  type TronPlusParticle,
} from "./tron-grid-data"
import { HeroTopPulseView } from "./hero-top-pulse-view"

export interface TronGridBackgroundViewProps {
  readonly variant: TronGridVariant
  readonly className?: string
  /** When true, skip the flat grid overlay (e.g. landing footer over a page-wide grid). */
  readonly skipFlatGrid?: boolean
}

const VARIANT_CONFIG = {
  homepage: {
    particles: HOMEPAGE_TRON_PLUS_PARTICLES,
    nodes: HOMEPAGE_TRON_GRID_NODES,
    svgId: "homepage",
    showPerspective: true,
    showVanishingLines: false,
  },
  footer: {
    particles: FOOTER_TRON_PLUS_PARTICLES,
    nodes: FOOTER_TRON_GRID_NODES,
    svgId: "footer",
    showPerspective: true,
    showVanishingLines: false,
  },
} as const

export function TronGridBackgroundView({ variant, className, skipFlatGrid = false }: TronGridBackgroundViewProps) {
  const config = VARIANT_CONFIG[variant]
  const viewBox = TRON_GRID_VIEWBOX[variant]

  return (
    <div
      className={cn(
        "tron-grid-atmosphere pointer-events-none overflow-hidden",
        variant === "homepage" ? "tron-grid-atmosphere--homepage fixed inset-0 z-0" : "tron-grid-atmosphere--footer absolute inset-0 z-0",
        className,
      )}
      aria-hidden
    >
      {!skipFlatGrid ? (
        <div className={cn("tron-grid-flat", variant === "homepage" ? "tron-grid-flat--homepage" : "tron-grid-flat--footer")} />
      ) : null}

      {config.showPerspective ? (
        <div
          className={cn(
            "tron-grid-perspective",
            variant === "homepage" ? "tron-grid-perspective--homepage" : "tron-grid-perspective--footer",
          )}
        />
      ) : null}


      <svg
        className={cn(
          "tron-grid-svg-layer absolute inset-0 h-full w-full",
          variant === "homepage" && "opacity-[0.45]",
        )}
        viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient
            id={`${config.svgId}-tron-vanish`}
            x1={viewBox.width / 2}
            y1={viewBox.height}
            x2={viewBox.width / 2}
            y2={viewBox.height * 0.36}
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#3b82f6" stopOpacity="0.18" />
            <stop offset="1" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
          <filter id={`${config.svgId}-plus-glow`} x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {config.showVanishingLines ? (
          <>
            {[-720, -480, -320, -160, 0, 160, 320, 480, 720].map((offset) => (
              <line
                key={`v-${offset}`}
                x1={960 + offset}
                y1={viewBox.height}
                x2={960 + offset * 0.12}
                y2={viewBox.height * 0.4}
                stroke={`url(#${config.svgId}-tron-vanish)`}
                strokeWidth="1"
              />
            ))}
            {[680, 720, 760, 800, 840].map((y) => (
              <line
                key={`h-${y}`}
                x1="120"
                y1={y}
                x2="1800"
                y2={y}
                stroke="#3b82f6"
                strokeOpacity={0.04 + (viewBox.height - y) / 6000}
                strokeWidth="1"
              />
            ))}
          </>
        ) : null}

        {config.nodes
          .filter((node) => variant !== "homepage" || node.cy >= 260)
          .map((node, index) => (
            <circle key={`node-${index}`} cx={node.cx} cy={node.cy} r={node.r} fill="#93c5fd" opacity={node.opacity} />
          ))}

        {config.particles
          .filter((particle) => variant !== "homepage" || particle.cy >= 260)
          .map((particle, index) => (
            <TronPlusParticleGraphic key={`plus-${index}`} particle={particle} glowFilterId={`${config.svgId}-plus-glow`} />
          ))}
      </svg>

      {variant === "homepage" ? <HeroTopPulseView /> : null}
    </div>
  )
}

function TronPlusParticleGraphic({
  particle,
  glowFilterId,
}: {
  readonly particle: TronPlusParticle
  readonly glowFilterId: string
}) {
  return (
    <g
      className="tron-grid-plus-particle"
      style={
        {
          "--tron-plus-opacity": particle.opacity,
          "--tron-plus-duration": `${particle.twinkleDuration}s`,
          "--tron-plus-delay": `${particle.twinkleDelay}s`,
        } as CSSProperties
      }
      filter={particle.glow ? `url(#${glowFilterId})` : undefined}
    >
      <line
        x1={particle.cx - particle.half}
        y1={particle.cy}
        x2={particle.cx + particle.half}
        y2={particle.cy}
        stroke={particle.stroke}
        strokeWidth="1"
      />
      <line
        x1={particle.cx}
        y1={particle.cy - particle.half}
        x2={particle.cx}
        y2={particle.cy + particle.half}
        stroke={particle.stroke}
        strokeWidth="1"
      />
    </g>
  )
}
